import React, { ReactNode, useEffect, useState } from "react";

import moment from "moment";

import { getDatePickerDisabledRange, getEnvironment, isImpersonating, isNullOrUndefined, useGetPracticeTillages, useTranslation } from "utils";

import { TILLAGE_DEPTH_RANGE_OPTIONS } from "consts";

import { ENUM_TILLAGE_SOIL_MIXING_TYPE, PracticeFCPO, ZERO_UUID } from "models";

import { Divider, Flex, Form, IconButton, InputNumber, OptGroup, Option, RangePicker, Select, Spin, Typography } from "components";

import { CALENDAR_YEAR_END, CALENDAR_YEAR_START } from "pages/app/all-field-view/constants/cropForm";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getHasSubmissionForSoilPlus3StartYear, getIsSoilPlus3DataCollectionEditable } from "pages/app/all-field-view/utils/dataEntryUtils";
import { InputRequirementConfig, isRequired, isRequiredForSaving, shouldShowInput } from "pages/app/all-field-view/utils/getInputRequirementConfig";

type TillageApplicationFormProps = {
  tillage: PracticeFCPO;
  index: number;
  onDelete: () => void;
  recommendedSeedingDate?: string;
  requirementConfig: InputRequirementConfig | null;
  optionalInputsAreShown: boolean;
  requirePreciseTillageDepth: boolean;
  // bulk flows pass a tag naming which of the selected crops this entry belongs to; nothing renders without it
  renderEntryTag?: (practice: PracticeFCPO) => React.ReactNode;
  // bulk flows may name an entry differently, e.g. "New Application" for one that exists on no crop yet
  renderEntryTitle?: (practice: PracticeFCPO) => React.ReactNode;
  requireTillageSoilMixing?: boolean;
  extraContent?: ReactNode;
  // locks every input of the entry, whatever its own verification state allows — for a caller that may not write to it at all
  isReadOnly?: boolean;
};

export const TillageApplicationForm = ({
  tillage,
  index,
  onDelete,
  recommendedSeedingDate,
  requirementConfig,
  optionalInputsAreShown,
  requirePreciseTillageDepth,
  renderEntryTag,
  renderEntryTitle,
  requireTillageSoilMixing,
  extraContent,
  isReadOnly = false,
}: TillageApplicationFormProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const { submissionStatusValue } = useSubmissionStatus();

  const TILLAGE = "tillage";
  const [pickerKey, setPickerKey] = useState(`${TILLAGE}-${index}`);
  const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();
  const lastSubmissionYear = submissionStatusValue?.lastSubmissionYear;
  const isDisabled =
    isReadOnly ||
    (getIsSoilPlus3DataCollectionEditable(tillage.soilPlus3DataCollection, lastSubmissionYear) ? false : tillage.isConfirmed && !isAdminOrImpersonator);
  const hasSubmissionForSoilPlus3StartYear = getHasSubmissionForSoilPlus3StartYear(lastSubmissionYear);

  const isDepthDisabled = (): boolean => isReadOnly || (!!tillage.isConfirmed && !isAdminOrImpersonator && hasSubmissionForSoilPlus3StartYear);
  const isApplicationDatesDisabled = (): boolean => {
    if (isReadOnly) return true;
    if (isRequired(requirementConfig?.dates) && !tillage.endDate) return false;
    if (isRequired(requirementConfig?.dates) && tillage.endDate) {
      if (getIsSoilPlus3DataCollectionEditable(tillage.applicationDatesSoilPlus3DataCollection, lastSubmissionYear)) return false;
    }
    return tillage.isConfirmed && !isAdminOrImpersonator && hasSubmissionForSoilPlus3StartYear;
  };

  useEffect(() => {
    // Force the RangePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.
    setPickerKey((prevState) => prevState + 1);
  }, [recommendedSeedingDate]);

  return (
    <Flex vertical className="tillage-application-form">
      <div className="form-container" data-testid="add-edit-tillage-application-form">
        {tillagePracticesQuery.isFetching ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <>
            <Divider className="divider" />
            <Form.Item name={[`${TILLAGE}-${index}`, "id"]} initialValue={tillage.id} hidden></Form.Item>
            <Form.Item name={[`${TILLAGE}-${index}`, "type"]} initialValue={tillage.type} hidden></Form.Item>
            <Flex justify="space-between">
              <Flex align="center" gap="0.5rem">
                <Typography variant="h5">{renderEntryTitle?.(tillage) ?? t("tillageForm.application", { applicationNumber: index + 1 })}</Typography>
                {renderEntryTag?.(tillage)}
              </Flex>
              <IconButton type="tertiary" size="small" iconName="tabler/trash" onClick={onDelete} disabled={isDisabled} />
            </Flex>
            <Select
              className="margin-top-200"
              dataTestId={`add-edit-tillage-form-tillage-type-selection-${index}`}
              label={t("tillageForm.tillageType")}
              name={[`${TILLAGE}-${index}`, "assignedPracticeId"]}
              initialValue={tillage.assignedPracticeId ?? null}
              value={tillage.assignedPracticeId ?? null}
              showSearch
              rules={[{ message: "", required: true }]}
              disabled={isDisabled}
            >
              <OptGroup>
                {tillagePracticesQuery.data &&
                  tillagePracticesQuery.data
                    .filter((practice) => practice.id !== ZERO_UUID)
                    .sort((a, b) => a.name?.localeCompare(b.name))
                    .map((option, i) => (
                      <Option data-testid={`tillage-selection-option-${i}`} key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))}
              </OptGroup>
            </Select>
            <Flex vertical gap="1rem" className="date-picker-container margin-top-100">
              <RangePicker
                key={pickerKey}
                dataTestId={`add-edit-tillage-form-date-range-${tillage.assignedPracticeId}-picker`}
                name={[`${TILLAGE}-${index}`, "dateRange"]}
                label={t("tillageForm.dateRange")}
                initialValue={[tillage.startDate ? moment(tillage.startDate) : null, tillage.endDate ? moment(tillage.endDate) : null]}
                defaultPickerValue={[
                  recommendedSeedingDate ? moment(recommendedSeedingDate) : null,
                  recommendedSeedingDate ? moment(recommendedSeedingDate) : null,
                ]}
                format={["DD.MM.YYYY"]}
                rules={[
                  { message: "", required: true },
                  {
                    validator: (_, value: moment.Moment[] | null) => {
                      if (!value?.[1]) {
                        return Promise.reject(new Error(t("tillageForm.errorMessages.dateRangeRequired")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                // Once submitted, the date can still be changed within the respective calendar year.
                disabledDate={(current) =>
                  getIsSoilPlus3DataCollectionEditable(tillage.soilPlus3DataCollection || tillage.applicationDatesSoilPlus3DataCollection, lastSubmissionYear)
                    ? false
                    : !isAdminOrImpersonator &&
                      tillage.isConfirmed &&
                      !!tillage.year &&
                      getDatePickerDisabledRange({
                        current,
                        startDate: `${CALENDAR_YEAR_START}${tillage.year}`,
                        endDate: `${CALENDAR_YEAR_END}${tillage.year}`,
                      })
                }
                disabled={isApplicationDatesDisabled()}
              />
            </Flex>

            {/* If there is already tillage depth data provided we show the input independant from config */}
            {(shouldShowInput(requirementConfig?.tillageDepth, optionalInputsAreShown) || !isNullOrUndefined(tillage.tillageDepth)) &&
              (requirePreciseTillageDepth ? (
                <InputNumber
                  className="margin-top-100"
                  dataTestId="add-edit-tillage-form-tillage-depth-input"
                  disabled={isDepthDisabled()}
                  initialValue={tillage.tillageDepth ?? null}
                  label={t("tillageForm.tillageDepth")}
                  max={100}
                  min={0}
                  name={[`${TILLAGE}-${index}`, "tillageDepth"]}
                  step={1}
                  suffix="cm"
                  rules={[
                    {
                      required: isRequiredForSaving(requirementConfig?.tillageDepth),
                      message: t("tillageForm.errorMessages.tillageDepthRequired"),
                    },
                  ]}
                />
              ) : (
                <Select
                  className="margin-top-100"
                  dataTestId="add-edit-tillage-form-tillage-depth-selection"
                  label={t("tillageForm.tillageDepth")}
                  name={[`${TILLAGE}-${index}`, "tillageDepth"]}
                  initialValue={tillage.tillageDepth ?? null}
                  value={tillage.tillageDepth ?? null}
                  rules={[
                    {
                      required: isRequiredForSaving(requirementConfig?.tillageDepth),
                      message: t("tillageForm.errorMessages.tillageDepthRequired"),
                    },
                  ]}
                  disabled={isDepthDisabled()}
                >
                  <OptGroup>
                    {TILLAGE_DEPTH_RANGE_OPTIONS.map((option) => (
                      <Option data-testid={`tillage-depth-selection-option-${option.value}`} key={option.value} value={option.value}>
                        {option.name}
                      </Option>
                    ))}
                  </OptGroup>
                </Select>
              ))}

            {requireTillageSoilMixing ? (
              <Select
                className="margin-top-100"
                dataTestId={`add-edit-tillage-form-tillage-soil-mixing-${index}`}
                label={t("tillageForm.tillageSoilMixingType")}
                name={[`${TILLAGE}-${index}`, "tillageSoilMixingType"]}
                initialValue={tillage.tillageSoilMixingType ?? null}
                value={tillage.tillageSoilMixingType ?? null}
                showSearch
                rules={[{ message: "", required: requireTillageSoilMixing }]}
                disabled={isDisabled}
              >
                <OptGroup>
                  {Object.values(ENUM_TILLAGE_SOIL_MIXING_TYPE).map((soilMixingTypeName, i) => (
                    <Option data-testid={`tillage-soil-mixing-option-${i}`} key={soilMixingTypeName} value={soilMixingTypeName}>
                      {t(`tillageForm.tillageSoilMixingTypeOptions.${soilMixingTypeName}`)}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            ) : null}
          </>
        )}
      </div>
      {extraContent}
    </Flex>
  );
};
